"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import PublicHeader from "@/components/shared/PublicHeader";
import { isPublicSignupOpen } from "@/lib/signup-access";
import {
  Calculator,
  DollarSign,
  Users,
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  Sparkles,
  CheckCircle2,
  Zap,
  BarChart3,
} from "lucide-react";

export default function ROICalculatorPage() {
  const [visitors, setVisitors] = useState(2000);
  const [clientValue, setClientValue] = useState(500);
  const [leadsMissed, setLeadsMissed] = useState(30);
  const [conversionRate, setConversionRate] = useState(15);
  const [animate, setAnimate] = useState(false);

  // Core calculation
  const missedLeads = Math.round(visitors * (leadsMissed / 100));
  const monthlyRecovery = missedLeads * clientValue * (conversionRate / 100);
  const yearlyRecovery = monthlyRecovery * 12;
  const dailyRecovery = monthlyRecovery / 30;

  // Trigger animation on value change (defer setState to avoid cascade render)
  useEffect(() => {
    const start = setTimeout(() => setAnimate(true), 0);
    const stop = setTimeout(() => setAnimate(false), 300);
    return () => { clearTimeout(start); clearTimeout(stop); };
  }, [visitors, clientValue, leadsMissed, conversionRate]);

  const formatCurrency = (num: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);

  const formatNumber = (num: number) =>
    new Intl.NumberFormat("en-US").format(num);

  return (
    <>
      <PublicHeader variant="minimal" links={[{href: "/pricing", label: "Pricing"}]} sticky maxWidth="max-w-6xl" />

      {/* H1 + hero subtitle rendered in server layout.tsx for SSR crawlability */}

      {/* Calculator Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Sliders Panel — 3 columns */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 sm:p-8 space-y-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Your Business Numbers</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Adjust the sliders to match your business</p>
                </div>
              </div>

              {/* Disclosure */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  <span className="font-semibold">How this works:</span> This calculator uses adjustable assumptions to estimate potential impact. Default conversion rate is 15%. Adjust the sliders below to match your business. Results are estimates only.
                </p>
              </div>

              {/* Slider 1: Monthly Website Visitors */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-400" />
                    </div>
                    Monthly Website Visitors
                  </label>
                  <span className="text-xl font-bold text-blue-400 bg-blue-500/10 px-4 py-1.5 rounded-xl tabular-nums">
                    {formatNumber(visitors)}
                  </span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={50000}
                  step={100}
                  value={visitors}
                  onChange={(e) => setVisitors(Number(e.target.value))}
                  className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>100</span>
                  <span>50,000</span>
                </div>
              </div>

              {/* Slider 2: Average Client Value */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-purple-400" />
                    </div>
                    Average Client Value
                  </label>
                  <span className="text-xl font-bold text-purple-400 bg-purple-500/10 px-4 py-1.5 rounded-xl tabular-nums">
                    {formatCurrency(clientValue)}
                  </span>
                </div>
                <input
                  type="range"
                  min={50}
                  max={25000}
                  step={50}
                  value={clientValue}
                  onChange={(e) => setClientValue(Number(e.target.value))}
                  className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>$50</span>
                  <span>$25,000</span>
                </div>
              </div>

              {/* Slider 3: % Leads Missed */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    </div>
                    % of Leads Missed
                  </label>
                  <span className="text-xl font-bold text-red-400 bg-red-500/10 px-4 py-1.5 rounded-xl tabular-nums">
                    {leadsMissed}%
                  </span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={80}
                  step={1}
                  value={leadsMissed}
                  onChange={(e) => setLeadsMissed(Number(e.target.value))}
                  className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>5%</span>
                  <span>80%</span>
                </div>
              </div>

              {/* Slider 4: Assumed Conversion Rate */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                    </div>
                    Assumed Conversion Rate
                  </label>
                  <span className="text-xl font-bold text-green-400 bg-green-500/10 px-4 py-1.5 rounded-xl tabular-nums">
                    {conversionRate}%
                  </span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={25}
                  step={1}
                  value={conversionRate}
                  onChange={(e) => setConversionRate(Number(e.target.value))}
                  className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-green-500"
                />
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>5% (Conservative)</span>
                  <span>25% (Optimistic)</span>
                </div>
              </div>
            </div>

            {/* Industry Stats */}
            <div className="bg-gray-900/50 rounded-2xl border border-gray-800/50 p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-4">Industry Averages</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-red-400">35%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leads missed after hours</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">78%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Customers go to competitor if no response in 5min</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">60%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Revenue recovered with 24/7 AI</p>
                </div>
              </div>
            </div>
          </div>

          {/* Results Panel — 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Big Revenue Recovery Card */}
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 rounded-2xl p-8 text-center">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTMwIDBMMCA2MGg2MEwzMCAweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
              <div className="relative">
                <Sparkles className="w-8 h-8 text-white/60 mx-auto mb-3" />
                <p className="text-sm font-medium text-white/70 mb-3 uppercase tracking-wider">
                  Yearly Revenue Recovery
                </p>
                <p
                  className={`text-5xl sm:text-6xl font-bold text-white mb-2 transition-transform duration-300 tabular-nums ${
                    animate ? "scale-105" : "scale-100"
                  }`}
                >
                  {formatCurrency(yearlyRecovery)}
                </p>
                <p className="text-sm text-white/60">
                  That&apos;s {formatCurrency(monthlyRecovery)}/month or {formatCurrency(dailyRecovery)}/day
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 space-y-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                How We Calculate This
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-800">
                  <span className="text-sm text-gray-400">Monthly visitors</span>
                  <span className="text-sm font-semibold text-white tabular-nums">{formatNumber(visitors)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-800">
                  <span className="text-sm text-gray-400">Leads missed ({leadsMissed}%)</span>
                  <span className="text-sm font-semibold text-red-400 tabular-nums">{formatNumber(missedLeads)} leads/mo</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-800">
                  <span className="text-sm text-gray-400">At {formatCurrency(clientValue)} per client ({conversionRate}% conv.)</span>
                  <span className="text-sm font-semibold text-yellow-400 tabular-nums">{formatCurrency(monthlyRecovery)}/mo</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-semibold text-white">Annual recovery potential</span>
                  <span className="text-lg font-bold text-green-400 tabular-nums">{formatCurrency(yearlyRecovery)}</span>
                </div>
              </div>
            </div>

            {/* What AI Can Do */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 space-y-3">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                What AI Agents Do For You
              </h3>
              {[
                "Respond to every inquiry in under 3 seconds",
                "Qualify leads and book appointments 24/7",
                "Answer FAQs from your knowledge base",
                "Capture contact details before visitors leave",
                "Route complex queries to your team",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <Link
              href="/signup"
              className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-4 rounded-xl transition-all text-lg shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
            >
              <Zap className="w-5 h-5" />
              {isPublicSignupOpen() ? "Start Recovering Revenue — Free" : "Request Access"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>

            <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
              {isPublicSignupOpen()
                ? "No credit card required. Guided setup available."
                : "Private preview — new access is by request."}
            </p>
          </div>
        </div>

        {/* Formula bar */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-6 py-3">
            <Calculator className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Formula: Visitors &times; (Missed% / 100) &times; Client Value &times; {conversionRate}% conversion &times; 12 months
            </span>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-600 dark:text-gray-400 text-center mt-4 max-w-xl mx-auto">
          * This tool provides estimates only. Aditya Labs does not guarantee any specific revenue outcome. Adjust the conversion rate slider to see how different assumptions affect projections.
        </p>
      </section>
    </>
  );
}
